import React, { useState, useCallback } from 'react';
import { Droplets } from 'lucide-react';

export interface HumiditySettingStyle01Props {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: number) => void;
  onChangeEnd?: (value: number) => void;
  label?: string;
  currentHumidity?: number;
}

/**
 * HumiditySetting Style 01 - Slider
 * Command: HumidityRelative
 * Horizontal slider for humidity setpoint with current reading display
 */
export const Style01_Slider: React.FC<HumiditySettingStyle01Props> = ({
  value,
  min = 40,
  max = 70,
  step = 5,
  disabled = false,
  loading = false,
  onChange,
  onChangeEnd,
  label = '目標濕度',
  currentHumidity
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  const displayValue = isDragging ? localValue : value;
  const percentage = ((displayValue - min) / (max - min)) * 100;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setLocalValue(newValue);
    onChange(newValue);
  }, [onChange]);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (onChangeEnd) {
      onChangeEnd(localValue);
    }
  }, [localValue, onChangeEnd]);

  return (
    <div className="space-y-3">
      {/* Current humidity display */}
      {currentHumidity !== undefined && (
        <div className="flex items-center justify-center gap-2 p-3 bg-blue-500/10 rounded-xl">
          <Droplets className="w-5 h-5 text-blue-400" />
          <span className="text-sm text-slate-300">目前室內濕度</span>
          <span className="text-lg font-bold text-blue-400">{currentHumidity}%</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-300">{label}</span>
        <span className="text-sm font-bold text-cyan-400">
          {loading ? '...' : `${displayValue}%`}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={displayValue}
        onChange={handleChange}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
        disabled={disabled || loading}
        className="w-full h-2 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: `linear-gradient(to right, #06b6d4 0%, #06b6d4 ${percentage}%, #334155 ${percentage}%, #334155 100%)`
        }}
      />
      <div className="flex justify-between text-xs text-slate-500">
        <span>{min}%</span>
        <span>{max}%</span>
      </div>
    </div>
  );
};

export default Style01_Slider;
